import type {AccumProfile, VolatilePattern} from './LFCPgHistoryAccumUtil';

// u フラグでは不要なエスケープが構文エラーになるため、クラスの外と中で別の集合をエスケープする
const escapeChar = (c: string) => c.replace(/[\\^$.*+?()[\]{}|/]/g, '\\$&');
const escapeClassChar = (c: string) => c.replace(/[\\\]\[^-]/g, '\\$&');

/** Python の `fnmatch.translate` と同じ規則で `[...]` の中身を正規表現の文字クラスにする */
const charClass = (chars: string[]): string => {
  const negate = chars[0] === '!';
  const body = negate ? chars.slice(1) : chars;
  if (!body.length) return negate ? '.' : '(?!)';
  const parts: string[] = [];
  for (let k = 0; k < body.length; ) {
    if (k + 2 < body.length && body[k + 1] === '-') {
      // 逆順の範囲（z-a）は Python と同じく何にも一致しない範囲として捨てる
      if (body[k] <= body[k + 2])
        parts.push(`${escapeClassChar(body[k])}-${escapeClassChar(body[k + 2])}`);
      k += 3;
    } else {
      parts.push(escapeClassChar(body[k]));
      k += 1;
    }
  }
  if (!parts.length) return negate ? '.' : '(?!)';
  return `[${negate ? '^' : ''}${parts.join('')}]`;
};

const translate = (pattern: string): string => {
  const pat = [...pattern];
  let out = '';
  for (let i = 0; i < pat.length; ) {
    const c = pat[i++];
    if (c === '*') {
      while (pat[i] === '*') i++;
      out += '.*';
    } else if (c === '?') {
      out += '.';
    } else if (c === '[') {
      let j = i;
      if (pat[j] === '!') j++;
      if (pat[j] === ']') j++;
      while (j < pat.length && pat[j] !== ']') j++;
      if (j >= pat.length) {
        out += '\\[';
      } else {
        out += charClass(pat.slice(i, j));
        i = j + 1;
      }
    } else {
      out += escapeChar(c);
    }
  }
  return `^(?:${out})$`;
};

/**
 * Python の `fnmatch.fnmatchcase` 相当（大文字小文字を区別し、`*` は `/` も含む任意の文字列に一致する）。
 * 分類ルールのパターンはゲートウェイ（Python）で評価されるため、画面でも同じ規則で照合する。
 */
export const fnmatchCase = (name: string, pattern: string): boolean =>
  new RegExp(translate(pattern), 'su').test(name);

const byPriority = (profiles: readonly AccumProfile[]) =>
  [...profiles].sort((a, b) =>
    a.priority !== b.priority ? a.priority - b.priority : a.id < b.id ? -1 : a.id > b.id ? 1 : 0
  );

/**
 * 端末・ファイルに適用される分類ルールを返す（ゲートウェイの `match_robot_profile` と同じ順序と除外）。
 * 有効なルールを (priority, id) 順に見て最初に一致したもの。パスに `PROG` 階層を含むファイルは対象外。
 */
export const matchProfile = (
  profiles: readonly AccumProfile[] | null | undefined,
  device: string,
  path: string
): AccumProfile | undefined => {
  if (!Array.isArray(profiles)) return undefined;
  if (path.replace(/\\/g, '/').toUpperCase().split('/').includes('PROG')) return undefined;
  return byPriority(profiles).find(
    p => p.enabled && fnmatchCase(device, p.device_pattern) && fnmatchCase(path, p.path_pattern)
  );
};

/**
 * ゲートウェイの `select_git_volatile_patterns` と同じ規則で、Git に送るファイルの揮発値ルールを返す。
 * 有効な git 行を (priority, id) 順に見て最初に一致したもの。PROG の除外はしない。
 */
export const selectGitVolatilePatterns = (
  profiles: readonly AccumProfile[] | null | undefined,
  device: string,
  path: string
): VolatilePattern[] => {
  if (!Array.isArray(profiles)) return [];
  const row = byPriority(profiles).find(
    p =>
      p.enabled &&
      p.handling === 'git' &&
      fnmatchCase(device, p.device_pattern) &&
      fnmatchCase(path, p.path_pattern)
  );
  return row?.volatile_patterns ?? [];
};

/** `devices/<端末>/<相対パス>` を端末と相対パスに分ける（ゲートウェイの照合単位） */
export const splitDeviceFilePath = (filePath: string): {device: string; path: string} | null => {
  const match = /^devices\/([^/]+)\/(.+)$/.exec(filePath);
  return match ? {device: match[1], path: match[2]} : null;
};
